"use client";

export const dynamic = "force-dynamic";

import Link from "next/link";
import { ArrowRight, Check, Brain, Eye, GitPullRequest, Camera, Clock, Bell, Zap, Bug, Shield, Lock, FileSearch, AlertTriangle, Plus, Minus, Loader2, KeyRound, ShieldAlert, ServerCrash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FreeScanWidget } from "@/components/marketing/free-scan-widget";
import React from "react";

// Animated scan steps
function ScanDemo() {
  const [step, setStep] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setStep((s) => (s + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    { label: "Scanning JS bundles for secrets...", type: "security", done: step > 0 },
    { label: "Checking security headers...", type: "security", done: step > 1 },
    { label: "Running QA agent on checkout flow", type: "qa", done: step > 2 },
    { label: "Bug found: button unclickable on mobile", type: "bug", done: step > 3 },
  ];

  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div
          key={i}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm transition-all duration-500 ${
            i === step
              ? s.type === "bug"
                ? "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"
                : "bg-muted border-border"
              : i < step
              ? "opacity-60 border-border"
              : "opacity-30 border-border"
          }`}
        >
          <div className={`h-5 w-5 shrink-0 rounded-full flex items-center justify-center ${
            i < step ? "bg-foreground" :
            i === step ? s.type === "bug" ? "bg-red-500" : "bg-foreground/20" : "bg-muted"
          }`}>
            {i < step
              ? <Check className="h-3 w-3 text-background" />
              : i === step && s.type === "bug"
              ? <Bug className="h-3 w-3 text-white" />
              : i === step
              ? <Loader2 className="h-3 w-3 text-foreground animate-spin" />
              : null
            }
          </div>
          <span className={`font-medium ${
            s.type === "bug" && i === step ? "text-red-600 dark:text-red-400" : ""
          }`}>{s.label}</span>
          {i === step && s.type !== "bug" && (
            <span className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider animate-pulse">running</span>
          )}
          {i === step && s.type === "bug" && (
            <span className="ml-auto text-[10px] font-bold text-red-600 uppercase tracking-wider">Critical</span>
          )}
        </div>
      ))}
    </div>
  );
}

// Simple custom accordion component
function SimpleAccordion({ children }: { children: React.ReactNode }) {
  return <div className="space-y-0">{children}</div>;
}

function SimpleAccordionItem({ value, title, children, icon: Icon }: { value: string; title: string; children: React.ReactNode; icon?: React.ComponentType<{ className?: string }> }) {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-5 text-left"
      >
        <div className="flex items-center gap-4">
          {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
          <span className="text-lg font-medium">{title}</span>
        </div>
        {isOpen ? (
          <Minus className="h-5 w-5 text-muted-foreground" />
        ) : (
          <Plus className="h-5 w-5 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <div className="pb-5 text-muted-foreground">
          {children}
        </div>
      )}
    </div>
  );
}

const FEATURES = [
  { icon: Brain, title: "AI-Powered Agent", desc: "GPT-4o vision navigates your app like a real user — finds bugs no script ever could." },
  { icon: ShieldAlert, title: "Security Scanning", desc: "Detects exposed API keys, broken auth, missing headers, CORS issues, and OWASP Top 10 vulnerabilities." },
  { icon: Eye, title: "Visual Testing", desc: "Catches layout shifts, rendering bugs, and broken UI that unit tests completely miss." },
  { icon: GitPullRequest, title: "GitHub Integration", desc: "Auto-run QA + security checks on every pull request. Get findings right in the PR." },
  { icon: Camera, title: "Screenshots & Evidence", desc: "Full visual proof for every step — share with your team or attach to bug tickets." },
  { icon: Clock, title: "Scheduled Scans", desc: "Run QA and security scans daily or weekly. Catch regressions before your users do." },
  { icon: KeyRound, title: "Secret Detection", desc: "Scans your public JS bundles for hardcoded API keys, tokens, and credentials before hackers find them." },
  { icon: Bell, title: "Instant Alerts", desc: "Slack and email notifications the moment a bug or vulnerability is detected." },
  { icon: FileSearch, title: "Combined Reports", desc: "One report covering both functional bugs and security findings — prioritized by severity." },
];

const TESTIMONIALS = [
  {
    quote: "We caught a checkout bug that would have cost us $50k in lost sales. The agent found it in 3 minutes.",
    author: "Sarah Chen",
    role: "CTO at ShopStream",
  },
  {
    quote: "Replaced our entire manual QA team with this. Saves 20 hours per week and catches more bugs.",
    author: "Marcus Johnson",
    role: "Engineering Lead at PayFlow",
  },
  {
    quote: "The GitHub integration is game-changing. Every PR now has visual proof that nothing broke.",
    author: "Elena Rodriguez",
    role: "Founder at DevSync",
  },
];

export const revalidate = 3600;

export default function LandingPage() {
  return (
    <div className="relative overflow-hidden">
      <div className="container mx-auto max-w-7xl px-4 lg:px-8">
        
        {/* Hero */}
        <section className="pt-20 pb-16 md:pt-28 md:pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted border border-border mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-foreground animate-pulse" />
              <span className="text-xs font-medium tracking-wide">QA Testing + Security Scanning</span>
            </div>

            <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight leading-[1] mb-6">
              Find bugs.
              <br />
              <span className="text-muted-foreground">Fix vulnerabilities.</span>
              <br />
              Ship faster.
            </h1>

            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10">
              The only platform that catches both functional bugs and security vulnerabilities — AI-powered, no setup required.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 mb-12">
              <Button asChild size="lg" className="rounded-full px-8 bg-foreground text-background hover:bg-foreground/90">
                <Link href="/register">
                  Start for free <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-8 border-border hover:bg-muted">
                <Link href="/pricing">See pricing</Link>
              </Button>
            </div>

            <div className="flex items-center justify-center gap-8 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> No credit card</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Free tier available</span>
              <span className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Setup in 2 minutes</span>
            </div>
          </div>

          {/* Free scan widget */}
          <div className="mt-14">
            <FreeScanWidget />
          </div>

          {/* Stats row */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg mx-auto">
            {[
              { value: "2-in-1", label: "QA + Security" },
              { value: "10k+", label: "Issues found" },
              { value: "3 min", label: "Avg. scan time" },
            ].map(({ value, label }) => (
              <div key={label} className="text-center">
                <p className="text-2xl font-bold">{value}</p>
                <p className="text-xs text-muted-foreground mt-1">{label}</p>
              </div>
            ))}
          </div>
        </section>


        {/* How It Works - Editorial Cards */}
        <section id="platform" className="py-12 md:py-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-sm text-muted-foreground uppercase tracking-wider">How it works</span>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
                Two minutes to your first test
              </h2>
              <p className="mt-4 text-muted-foreground">
                No setup required. The agent does the work.
              </p>
            </div>
            
            <div className="space-y-6">
              {[
                { step: "01", title: "Add your project", desc: "Paste your staging URL. No SDK, no scripts, no infrastructure to set up." },
                { step: "02", title: "Agent tests like a human", desc: "AI navigates pages, fills forms, clicks buttons, and logs in — just like a real user would." },
                { step: "03", title: "Get a full bug report", desc: "Screenshots, recordings, and actionable findings delivered in seconds." },
              ].map(({ step, title, desc }) => (
                <div key={step} className="group flex gap-6 p-6 rounded-xl border border-border hover:border-foreground/20 transition-colors">
                  <span className="text-4xl font-bold text-muted-foreground/30 group-hover:text-primary/50 transition-colors">{step}</span>
                  <div>
                    <h3 className="font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section id="solutions" className="py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="text-sm text-muted-foreground uppercase tracking-wider">Features</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
              QA + Security. One platform.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Stop paying for two separate tools. Axlynq finds broken flows <em>and</em> security vulnerabilities in a single scan.</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="group p-6 rounded-xl border border-border hover:border-foreground/20 transition-all">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-foreground/5 group-hover:bg-primary/10 transition-colors">
                  <Icon className="h-5 w-5 text-foreground/70 group-hover:text-primary transition-colors" />
                </div>
                <h3 className="font-semibold mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Platform Intro - Large Typography */}
        <section className="py-12 md:py-16 border-y border-border">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[0.95]">
                INTRODUCING
                <br />
                AXLYNQ PLATFORM
              </h2>
            </div>
            
            <div className="lg:pt-4">
              <SimpleAccordion>
                <SimpleAccordionItem value="discover" title="Discover" icon={Eye}>
                  <p className="text-muted-foreground leading-relaxed">
                    Automatically scan your entire application. Our AI agent maps all pages, 
                    identifies critical user flows, and detects visual regressions without 
                    writing a single test script.
                  </p>
                </SimpleAccordionItem>
                
                <SimpleAccordionItem value="mitigate" title="Mitigate" icon={Shield}>
                  <p className="text-muted-foreground leading-relaxed">
                    Catch bugs before they reach production. Get instant alerts with 
                    screenshots, reproduction steps, and severity ratings. Fix issues 
                    while they&apos;re still cheap to resolve.
                  </p>
                </SimpleAccordionItem>
                
                <SimpleAccordionItem value="govern" title="Govern" icon={Lock}>
                  <p className="text-muted-foreground leading-relaxed">
                    Maintain quality standards across your organization. Set testing policies, 
                    track team performance, and ensure every deployment meets your bar.
                  </p>
                </SimpleAccordionItem>
                
                <SimpleAccordionItem value="protect" title="Protect" icon={Bug}>
                  <p className="text-muted-foreground leading-relaxed">
                    Guard your revenue and reputation. Catch checkout bugs, broken forms, 
                    and accessibility issues before customers do. Sleep better knowing 
                    your app is tested 24/7.
                  </p>
                </SimpleAccordionItem>
              </SimpleAccordion>
            </div>
          </div>
        </section>

        {/* Solution Cards */}
        <section className="py-12 md:py-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: FileSearch, 
                title: "Continuity", 
                desc: "Never miss a regression. Continuous testing catches issues the moment they appear, not after customers complain." 
              },
              { 
                icon: Zap, 
                title: "Velocity", 
                desc: "Ship faster with confidence. Automated QA means no more waiting for manual testing cycles." 
              },
              { 
                icon: AlertTriangle, 
                title: "Visibility", 
                desc: "See what others miss. Visual testing catches layout shifts and UI bugs invisible to traditional tests." 
              },
              { 
                icon: Shield, 
                title: "Reliability", 
                desc: "Sleep soundly. 99.9% uptime monitoring with instant alerts when something goes wrong." 
              },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="group p-6 rounded-xl border border-border hover:border-foreground/20 transition-all bg-muted/30">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-foreground text-background">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="text-sm text-muted-foreground uppercase tracking-wider">Testimonials</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
              Loved by engineering teams
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ quote, author, role }) => (
              <div key={author} className="p-6 rounded-xl border border-border hover:border-foreground/20 transition-colors">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 fill-foreground" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">&ldquo;{quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-foreground/10 flex items-center justify-center text-xs font-medium">
                    {author.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{author}</p>
                    <p className="text-xs text-muted-foreground">{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Security + QA findings preview */}
        <section className="py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="text-sm text-muted-foreground uppercase tracking-wider">Sample Findings</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">What Axlynq finds in minutes</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                type: "security",
                severity: "critical",
                icon: KeyRound,
                title: "Hardcoded Stripe key in JS bundle",
                desc: "sk_live_... found exposed in main.js. Any visitor can extract and use your production API key.",
                tags: ["Secret Exposed", "OWASP A02", "Immediate Action"],
              },
              {
                type: "security",
                severity: "high",
                icon: ShieldAlert,
                title: "Missing Content-Security-Policy header",
                desc: "No CSP header detected. Your app is vulnerable to XSS attacks that can steal user sessions.",
                tags: ["Security Header", "OWASP A05", "Easy Fix"],
              },
              {
                type: "qa",
                severity: "critical",
                icon: Bug,
                title: "Checkout button unclickable on mobile",
                desc: 'The \"Complete purchase\" button is unresponsive on viewports < 375px. Agent tapped 3 times with no response.',
                tags: ["Step 7 screenshot", "Mobile bug", "Revenue impact"],
              },
              {
                type: "qa",
                severity: "high",
                icon: ServerCrash,
                title: "Login form accepts empty password",
                desc: "Submitting the login form with an empty password field triggers a 500 server error instead of validation.",
                tags: ["Auth flow", "Server error", "Reproduction steps"],
              },
            ].map(({ severity, icon: Icon, title, desc, tags }) => (
              <div key={title} className="rounded-2xl border border-border bg-card p-6 flex items-start gap-4">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  severity === "critical" ? "bg-destructive/10" : "bg-foreground/5"
                }`}>
                  <Icon className={`h-5 w-5 ${
                    severity === "critical" ? "text-destructive" : "text-foreground/70"
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      severity === "critical" ? "bg-destructive/10 text-destructive" : "bg-foreground/10 text-foreground"
                    }`}>{severity}</span>
                  </div>
                  <p className="font-semibold mb-1">{title}</p>
                  <p className="text-sm text-muted-foreground mb-3">{desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span key={tag} className="rounded-full border border-border px-3 py-1 text-xs font-medium bg-background">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="text-sm text-muted-foreground uppercase tracking-wider">Why Axlynq</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">One tool vs two bills</h2>
            <p className="mt-3 text-muted-foreground max-w-md mx-auto text-sm">
              Most teams pay for a QA tool and a separate security scanner. Axlynq replaces both.
            </p>
          </div>

          <div className="rounded-2xl border border-border overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-4 bg-muted/50 border-b border-border">
              <div className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Capability</div>
              {[
                { name: "Axlynq", highlight: true },
                { name: "Security-only tools", highlight: false },
                { name: "Manual QA", highlight: false },
              ].map(({ name, highlight }) => (
                <div key={name} className={`px-5 py-4 text-center text-sm font-semibold ${highlight ? "text-foreground" : "text-muted-foreground"}`}>
                  {name}
                  {highlight && <span className="ml-1.5 text-[10px] font-bold bg-foreground text-background px-1.5 py-0.5 rounded-full">YOU ARE HERE</span>}
                </div>
              ))}
            </div>

            {[
              { label: "QA / functional bug detection", axlynq: true, breachme: false, manual: true },
              { label: "AI browser agent testing", axlynq: true, breachme: false, manual: false },
              { label: "Security vulnerability scanning", axlynq: true, breachme: true, manual: false },
              { label: "Exposed secrets detection", axlynq: true, breachme: true, manual: false },
              { label: "GitHub PR integration", axlynq: true, breachme: false, manual: false },
              { label: "Screenshots & evidence", axlynq: true, breachme: false, manual: "Sometimes" },
              { label: "Combined QA + security report", axlynq: true, breachme: false, manual: false },
              { label: "Setup time", axlynq: "2 min", breachme: "2 min", manual: "Days" },
              { label: "Monthly cost", axlynq: "From $0", breachme: "$49+", manual: "$5k+ (salary)" },
            ].map((row, i, arr) => (
              <div
                key={row.label}
                className={`grid grid-cols-4 items-center ${i < arr.length - 1 ? "border-b border-border/50" : ""}`}
              >
                <div className="px-5 py-3.5 text-sm text-foreground/80">{row.label}</div>
                {[row.axlynq, row.breachme, row.manual].map((val, j) => (
                  <div key={j} className={`px-5 py-3.5 text-center ${j === 0 ? "bg-foreground/[0.02]" : ""}`}>
                    {typeof val === "boolean" ? (
                      val
                        ? <Check className="h-4 w-4 mx-auto text-foreground" />
                        : <span className="text-muted-foreground/30 text-lg leading-none">—</span>
                    ) : (
                      <span className="text-sm font-medium">{val}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16">
          <div className="rounded-3xl bg-foreground text-background p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Find bugs. Fix vulnerabilities. Ship faster.
            </h2>
            <p className="text-background/70 mb-8 max-w-md mx-auto">
              The only tool that tests your app for both broken flows and security risks. Free to start.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8 bg-background text-foreground hover:bg-background/90">
                <Link href="/register">
                  Get started free <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-full px-8 text-background hover:bg-background/20">
                <Link href="/pricing">View pricing</Link>
              </Button>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
